import type { CatalogItem, OccasionType } from '@forbiz/contracts'

import { pointPrice, type PointRates } from '../lib/pointPrice.js'
import s from './ProductInfo.module.css'

/**
 * 상품 신뢰정보(BIZ-103/122) — 상품 상세 페이지와 주문서 상품 상세 시트가 같은 걸 그린다.
 * 유형·이름·가격(+포인트 옵션별 결제 금액) + 크기·구성·배송·유의사항.
 * 배송비·환불은 아직 상품 데이터에 없어 정책 정적 텍스트로 둔다(후속 데이터화).
 */

export const OCCASION_LABEL: Record<OccasionType, string> = {
  FUNERAL: '근조',
  CELEBRATION: '축하',
  GIFT: '난·기타',
}

/** 유형별 구성·포함 안내(정적). 운영자가 상품에 값을 채우면 그 값이 이긴다. */
function inclusionOf(occasion: OccasionType): string {
  if (occasion === 'FUNERAL') return '기본 리본 2줄 무료 · 근조 표기 · 지역별 실물은 소폭 다를 수 있음'
  if (occasion === 'CELEBRATION') return '기본 리본 2줄 무료 · 축하 표기 · 지역별 실물은 소폭 다를 수 있음'
  return '기본 카드 무료 · 지역별 실물은 소폭 다를 수 있음'
}

export function ProductInfo({
  product,
  rates,
  as: Heading = 'h1',
}: {
  product: CatalogItem
  /** 회사 포인트 율. 주면 정가 아래에 즉시할인가·적립 포인트를 함께 보여준다. */
  rates?: PointRates | null | undefined
  /** 페이지에선 h1, 시트 안에선 h3 등 — 문서 구조에 맞춘다. */
  as?: 'h1' | 'h2' | 'h3'
}) {
  const pp = rates ? pointPrice(product.price, rates) : null
  return (
    <>
      <div className={s.head}>
        <span className={s.occ}>{OCCASION_LABEL[product.occasionType]}</span>
        <Heading className={s.name}>{product.name}</Heading>
        <div className={s.price}>{product.price.toLocaleString('ko-KR')}원</div>
        {pp && (pp.discount > 0 || pp.earn > 0) ? (
          <ul className={s.options} aria-label="결제 방법별 금액">
            <li>
              <span>적립 선택</span>
              <span>
                {pp.list.toLocaleString('ko-KR')}원 결제{pp.earn > 0 ? ` · ${pp.earn.toLocaleString('ko-KR')}P 적립` : ''}
              </span>
            </li>
            {pp.discount > 0 ? (
              <li>
                <span>즉시할인 선택</span>
                <span className={s.optStrong}>{pp.discounted.toLocaleString('ko-KR')}원 결제</span>
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>

      <dl className={s.rows}>
        <div className={s.row}>
          <dt>크기</dt>
          <dd>{product.summary || '크기 정보 준비 중'}</dd>
        </div>
        <div className={s.row}>
          <dt>구성 · 포함</dt>
          {/* 여러 줄 입력 보존 */}
          <dd style={{ whiteSpace: 'pre-line' }}>{product.inclusion ?? inclusionOf(product.occasionType)}</dd>
        </div>
        <div className={s.row}>
          <dt>배송</dt>
          <dd style={{ whiteSpace: 'pre-line' }}>
            {product.deliveryNote ??
              '수도권 배송비 포함 · 그 외 지역 추가(주문 시 안내) · 발인/행사 맞춤(공급처 확정 시 최종)'}
          </dd>
        </div>
        <div className={s.row}>
          <dt>유의사항</dt>
          <dd style={{ whiteSpace: 'pre-line' }}>
            {product.caution ?? '배송 시작 전엔 직접 수정 가능 · 취소·환불은 전화 문의'}
          </dd>
        </div>
      </dl>
    </>
  )
}
